/**
 * AppSync handles updates to the Vuex store.  Its primary
 * function is to update state when the client receiveds an
 * appsync notification from the server
 */

import { store } from '@/store'
import router from '@/router/router'
import AppSyncNotification from '@/types/AppSyncNotification.interface'
import Message from '@/types/Message.interface'
import User, { UserRole } from '@/types/User.interface'
import filters from '@/utility/filters'
import Tenant from '@/types/Tenant.interface'
import csvhelper from '@/plugins/csvhelper'
import Group from '@/types/Group.interface'
import Broadcast from '@/types/Broadcast.interface'
import Notification from '@/types/Notification.interface'
import UserNotification from '@/types/UserNotification.interface'
import Analytics from './Analytics'

//  Checks if the user passed in is the auth user and if they are, use the callback passed in
const authSync = (user: User, callback: Function): void => {
    //  Grab our auth user
    const auth = store.getters.user

    //  Check if the auth user matches our data
    const match = auth.id === user.id

    //  If they don't match, we are done
    if (!match) return

    //  Else, call the callback
    callback(auth)
}
//  Syncs our group lists
const groupsSync = (groups: Group[], global: boolean = true): void => {
    //  If global is true then we update our global dataset
    if (global) store.commit('groupsPush', groups)

    //  Update group selector
    store.commit('groupSelectorGroupsPush', groups)

    //  Update our groups in our groups (lists) module
    store.commit('listsGroupsPush', groups)

    //  Update our selected group and list of groups in broadcasts module
    store.commit('broadcastsViewGroupsPush', groups)

    //  Add it to our person groups
    store.commit('personGroupsPush', groups)

    //  Update our selected group in the autoresponders module
    store.commit('autorespondersSelectedGroupSync', groups)

    //  Update our bucket groups
    store.commit('groupsBucketsPush', groups)

    //  Update our inbox group
    store.commit('inboxSelectedBucketSync', groups)
}
//  Updates our message and channel lists with new messages/channels
const messagesSync = (message: Message): void => {
    //  Update global messages
    store.commit('messagesPush', [message])

    //  Sync our inbox channels (this handles channel list)
    store.commit('inboxMessagePostChannelSync', message)

    //  Sync our inbox messages (this handles selected channel)
    store.commit('inboxMessagePostMessageSync', message)

    //  Sync our teammates (this is a special sync that handles updating channel as well)
    //  We don't require 2 of these like above because we don't display "last_message" in teammates
    store.commit('teammatesMessagePost', message)

    //  Update messages table
    store.commit('messagesTableMessagesSync', [message])

    //  Update payments messages
    store.commit('paymentsSelectedInvoiceMessagesSync', [message])
}

//  Define our app sync event handlers
const handlers = {
    /**
     * ***************************************
     * Broadcasts
     * ***************************************
     */
    broadcastsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastsDeleted', notification)

        //  Get the broadcast ids from the notification
        const broadcastIds = notification.removed_ids

        //  Update our global dataset
        store.commit('broadcastsRemove', broadcastIds)

        //  Update our broadcasts view
        store.commit('broadcastsViewBroadcastsRemove', broadcastIds)
    },
    async broadcastDeliveryReceipts(notification: AppSyncNotification): Promise<void> {
        store.getters.appDebug && console.log('APPSYNC: broadcastDeliveryReceipts', notification)

        //  Grab our message ids
        const ids = notification.message_ids

        //  Get the messages
        const messages = await store.dispatch('messagesWhereIn', ids)

        //  Update global messages
        store.commit('messagesPush', messages)

        //  Update messages table
        store.commit('messagesTableMessagesSync', messages)

        //  Update payments messages
        store.commit('paymentsSelectedInvoiceMessagesSync', messages)

        //  Update broadcasts sent messages
        store.commit('broadcastsViewSentMessagesSync', messages)

        //  Update broadcasts failed messages
        store.commit('broadcastsViewFailedMessagesSync', messages)
    },
    //  This is when the broadcast payload >= 10kb
    async broadcastMessagesSentDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastMessagesSentDefault', notification)

        //  Grab our notification data
        const ids = notification.message_ids

        //  Grab our messages from the data and store the additional attributes in attr
        const messages = await store.dispatch('messagesWhereIn', ids)

        //  Sync the messages
        messages.forEach((x: Message) => {
            messagesSync(x)
        })

        //  Sort our inbox channels
        store.commit('inboxChannelsSort')

        //  Grab our broadcast id
        const broadcastId = notification.broadcast_id

        //  Grab the currently selected broadcast
        const selected = store.getters.broadcastsViewSelectedBroadcast

        //  If there is no selected, do nothing
        if (!selected) return

        //  If the selected id does NOT match our broadcast id, do nothing
        if (selected.id !== broadcastId) return

        //  Else, do some app sync updates for our broadcasts
        store.commit('broadcastsViewSentMessagesSync', messages)

        //  Update broadcasts failed messages
        store.commit('broadcastsViewFailedMessagesSync', messages)
    },
    //  This is the improved app sync handling for when broadcast payload < 10kb
    broadcastMessagesSent(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastMessagesSent', notification)

        //  Grab our notification data
        const data = notification.data

        //  Grab our messages from the data and store the additional attributes in attr
        const { messages, ...attr } = data

        //  Our array of messages
        const msgArr = []

        //  Iterate through our messages
        messages.forEach((x: Message) => {
            //  Our message will be the current entry + additional attributes
            const msg = {...x, ...attr}

            //  Sync our inbox channels (this handles channel list)
            store.commit('inboxMessagePostChannelSync', msg)

            //  Sync our inbox messages (this handles selected channel)
            store.commit('inboxMessagePostMessageSync', msg)

            //  Sync our teammates (this is a special sync that handles updating channel as well)
            //  We don't require 2 of these like above because we don't display "last_message" in teammates
            store.commit('teammatesMessagePost', msg)

            //  Append this to our array
            msgArr.push(msg)
        })

        //  Sort our inbox channels
        store.commit('inboxChannelsSort')

        //  Update global messages
        store.commit('messagesPush', msgArr)

            //  Update messages table
        store.commit('messagesTableMessagesSync', msgArr)

        //  Update payments messages
        store.commit('paymentsSelectedInvoiceMessagesSync', msgArr)
    },
    //  This is the improved app sync handling for when broadcast payload < 10kb
    broadcastMessagesSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastMessagesSaved', notification)

        //  Grab our notification data
        const data = notification.data

        //  Grab our messages from the data and store the additional attributes in attr
        const { messages, ...attr } = data

        //  Our array of messages
        const msgArr = []

        //  Iterate through our messages
        messages.forEach((x: Message) => {
            //  Our message will be the current entry + additional attributes
            const msg = {...x, ...attr}

            //  Sync our inbox channels (this handles channel list)
            store.commit('inboxMessagePostChannelSync', msg)

            //  Sync our inbox messages (this handles selected channel)
            store.commit('inboxMessagePostMessageSync', msg)

            //  Sync our teammates (this is a special sync that handles updating channel as well)
            //  We don't require 2 of these like above because we don't display "last_message" in teammates
            store.commit('teammatesMessagePost', msg)

            //  Append this to our array
            msgArr.push(msg)
        })

        //  Sort our inbox channels
        store.commit('inboxChannelsSort')

        //  Update global messages
        store.commit('messagesPush', msgArr)

        //  Update messages table
        store.commit('messagesTableMessagesSync', msgArr)

        //  Update payments messages
        store.commit('paymentsSelectedInvoiceMessagesSync', msgArr)

        //  Update broadcasts sent messages
        store.commit('broadcastsViewSentMessagesSync', msgArr)

        //  Update broadcasts failed messages
        store.commit('broadcastsViewFailedMessagesSync', msgArr)
    },
    broadcastSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastSaved', notification)

        //  Grab the updated broadcast from our notification
        const data = notification.data

        //  Update our global dataset
        store.commit('broadcastsPush', [data])

        //  Update the broadcasts view (sync instead of push means it only gets updated, not added)
        store.commit('broadcastsViewBroadcastsSync', [data])
    },
    broadcastTotalUpdated(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: broadcastTotalUpdated', notification)

        //  Grab our broadcast id
        const broadcastId = notification.broadcast_id

        //  Check if we have a selected broadcast
        const selected = store.getters.broadcastsViewSelectedBroadcast

        //  If we don't, return
        if (!selected) return

        //  Else, check if the ids match
        const match = broadcastId === selected.id

        //  If they don't match, return
        if (!match) return

        //  Grab the failed and sent totals from our notification
        const failed = notification.failed
        const sent = notification.sent

        //  Update our totals
        store.commit('broadcastsViewFailedMessagesTotalSet', failed)
        store.commit('broadcastsViewSentMessagesTotalSet', sent)
    },
    /**
     * ***************************************
     * Campaigns
     * ***************************************
     */
    campaignsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: campaignsSaved', notification)

        //  Get the campaign ids from the notification
        const campaignIds = notification.removed_ids

        //  Update our global dataset
        store.commit('campaignsRemove', campaignIds)

        //  Update our autoresponders module
        store.commit('autorespondersCampaignsRemove', campaignIds)
    },
    campaignSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: campaignSaved', notification)

        //  Grab our campaign data
        const data = notification.data

        //  Update the global dataset
        store.commit('campaignsPush', [data])

        //  Update the autoresponders module (sync instead of push means it only gets updated, not added)
        store.commit('autorespondersCampaignsSync', [data])
    },
    /**
     * ***************************************
     * Channels
     * ***************************************
     */
    channelsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: channelsDeleted', notification)

        //  Get the channel ids from the notification
        const channelIds = notification.removed_ids

        //  Update the inbox
        store.commit('inboxChannelsRemove', channelIds)

        //  Update our teammates view
        store.commit('teammatesSelectedChannelRemove', channelIds)
    },
    channelsDeletedDefault(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: channelsDeletedDefault', notification)

        //  Refresh our channels
        store.dispatch('inboxChannelsPaginate')
    },
    //  Handles inbox channels update
    inboxChannelSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: inboxChannelSaved', notification)

        //  Grab our channel data
        const data = notification.data

        //  Update the inbox
        store.commit('inboxChannelsSync', [data])
    },
    /**
     * ***************************************
     * Chatbots
     * ***************************************
     */
    chatbotDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: chatbotDeleted')

        //  Grab the chatbot id from our notification
        const chatbotIds = notification.removed_ids

        //  Else, remove the chatbot from our global set
        store.commit('chatbotsRemove', chatbotIds)

        //  Remove the chatbot from our chatbots view
        store.commit('chatbotsViewChatbotsRemove', chatbotIds)
    },
    chatbotSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: chatbotSaved')

        //  Grab the chatbot from our notification
        const data = notification.data

        //  Update our chatbots global
        store.commit('chatbotsPush', [data])

        //  Update the chatbots view
        store.commit('chatbotsViewChatbotsPush', [data])
    },
    /**
     * ***************************************
     * Client
     * ***************************************
     */
    clientDeployed(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: clientDeployed', notification)

        //  If we're running on a Native App (iOS, Android), do nothing
        if (store.getters.isNativeApp) return

        //  Else, get the 'force' option from the notification
        const force = notification.force

        //  if the 'force' option is true, reload the window
        if (force) return location.reload()

        //  Else, get the current git commit hash
        const current = store.getters.appVersion

        //  Get the commit has from the notification
        const commit = notification.commit

        //  Determine if the client needs to be reloaded
        const shouldReload = current != commit

        //  If the client should NOT reload, do nothing
        if (!shouldReload) return

        //  Else, pop the reload notification
        store.commit('clientDeployedPopupSet', true)
    },
    /**
     * ***************************************
     * Consent
     * ***************************************
     */
    consentSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: consentSaved', notification)

        //  Grab the consent from the notification
        const data = notification.data

        //  Update our consent
        store.commit('consentSet', data)
    },
    /**
     * ***************************************
     * CSV
     * ***************************************
     */
    csvExportProgress(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: csvExportProgress', notification)

        //  If there is no notification, do nothing
        if (!notification) return

        //  Grab our notification percent
        const percent = notification.percent

        //  If we are at 100% then we are done
        if (percent === 100) return store.commit('csvExportProgressSet', null)

        //  Else, we need to set our progress
        const progress = {
            percent: notification.percent,
            processed: notification.processed,
            total: notification.total,
            message: notification.message || null
        }

        //  Set the progress
        store.commit('csvExportProgressSet', progress)
    },
    async csvImportCompleted(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: csvImportCompleted', notification)

        //  Our import is complete so set progress to null
        store.commit('csvImportProgressSet', null)

        //  Grab our group id if it exists
        const groupId = notification.group_id

        //  Check if our sender id matches the current user
        const sameUser = notification.sender_id == store.getters.user.id

        if (sameUser) csvhelper.importShowReport(notification.report)

        //  If goto query param is present, route to it with group attached
        if (window.root.$route.query.goto) {
             //  Find our group
             const group = await store.dispatch('groupsFind', groupId)

             //  Sync our changes
             await groupsSync([group])

            //  Route to our goto route
            router.push(`${window.root.$route.query.goto}?group=${groupId}`)
        }

        //  Regardless of what happens, we should refresh our contacts and groups
        store.dispatch('personsPaginate')
        store.dispatch('groupsGet')
    },
    csvImportFailed(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: csvImportFailed', notification)

        //  Set our progress to null since this failed
        store.commit('csvImportProgressSet', null)
    },
    csvImportProgress(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: csvImportProgress', notification)

        //  If there is no notification, do nothing
        if (!notification) return

        //  Grab our notification percent
        const percent = notification.percent

        //  If percent is 100 then we are complete
        if (percent === 100) {
            //  Import is complete, set progress to null
            store.commit('csvImportProgressSet', null)

            //  Paginate persons and groups since we did a btach import
            store.dispatch('personsPaginate', { filter: 'all', page: 1, letter: null })
            store.dispatch('groupsGet')
        } else {
            //  Extract progress valiables from the notification
            const progress = {
                percent: notification.percent,
                processed: notification.processed,
                total: notification.total,
                message: notification.message || null
            }

            //  Set the progress
            store.commit('csvImportProgressSet', progress)
        }
    },
    /**
     * ***************************************
     * Data Provider
     * ***************************************
     */
    dataProviderDisconnected(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: dataProviderDisconnected', notification)

        //  Grab tenant id from notification
        const tenantId = notification.tenant_id

        //  Refresh our tenant
        store.dispatch('tenantGet', tenantId)

        //  Refresh data prviders
        store.dispatch('qboAccountGet')
        store.dispatch('xeroConnection')

        //  Get our connection
        store.commit('qboConnectionSet', false)
        store.commit('xeroConnectionSet', false)
    },
    dataProviderSyncCompleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: dataProviderSyncCompleted', notification)

        //  Finish sync progress
        store.commit('dataProviderSyncProgressSet', null)

        //  Refresh data prviders
        store.dispatch('qboAccountGet')
        store.dispatch('xeroConnection')

        //  Refresh contacts and groups
        store.dispatch('personsPaginate', { filter: 'all', page: 1, letter: null })
        store.dispatch('groupsGet')
    },
    dataProviderSyncFailed(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: dataProviderSyncFailed', notification)

        //  Finish the data provider progress
        store.commit('dataProviderSyncProgressSet', { message: 'Sync Failed', percent: 100 })
    },
    dataProviderSyncProgress(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: dataProviderSyncProgress', notification)

        //  Report the sync progress
        store.commit('dataProviderSyncProgressSet', notification)
    },
    /**
     * ***************************************
     * Extension Options
     * ***************************************
     */
    extensionOptionDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: extensionOptionDeleted', notification)

        //  Grab the extension option id
        const optionIds = notification.removed_ids

        //  Update our extension options
        store.commit('extensionOptionsRemove', optionIds)

        //  Update our extension options view
        store.commit('customFieldsExtensionOptionsRemove', optionIds)
    },
    extensionOptionSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: extensionOptionSaved', notification)

        //  When making changes to our extension options, it automatically sends out a personSaved event
        //  So we don't have to worry about person states here

        //  Grab our updated extension data
        const data = notification.data

        //  Update extension options
        store.commit('extensionOptionsPush', [data])

        //  Update extension options view
        store.commit('customFieldsExtensionOptionsPush', [data])
    },
    /**
     * ***************************************
     * Forms
     * ***************************************
     */
    formsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: formsDeleted', notification)

        //  Grab our form ids
        const formIds = notification.removed_ids

        //  Update our global state
        store.commit('formsRemove', formIds)

        //  Update our forms view store
        store.commit('formsViewFormsRemove', formIds)
    },
    formSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: formSaved', notification)

        //  Grab our form data
        const data = notification.data

        //  Else, update our global state
        store.commit('formsPush', [data])

        //  Update our forms view state
        store.commit('formsViewFormsPush', [data])
    },
    formSubmissionsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: formSubmissionsDeleted', notification)

        //  Get the deleted ids
        const submissionIds = notification.removed_ids

        //  Update our global state
        store.commit('formSubmissionsRemove', submissionIds)

        //  Update our forms view
        store.commit('formsViewSubmissionsRemove', submissionIds)
    },
    formSubmissionSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: formSubmissionsSaved', notification)

        //  Grab our submission data
        const data = notification.data

        //  Else, update our global set
        store.commit('formSubmissionsPush', [data])

        //  Update our forms view
        store.commit('formsViewSubmissionsSync', [data])
    },
    /**
     * ***************************************
     * Groups
     * ***************************************
     */
    groupsCountUpdated(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupsCountUpdated', notification)

        //  Get the data from our notification
        const data: Group[] = notification.data

        //  Our data consists of ids and persons counts so we can just update our store
        groupsSync(data, false)
    },
    groupsCountUpdatedDefault(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupsCountUpdatedDefault', notification)

        //  Refresh our groups
        store.dispatch('groupsGet')
    },
    groupsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupsDeleted', notification)

        //  Get the group ids from the notification
        const groupIds = notification.removed_ids

        //  Update global dataset
        store.commit('groupsRemove', groupIds)

        //  Update group selector
        store.commit('groupSelectorGroupsRemove', groupIds)

        //  Update our groups in our groups (lists) module
        store.commit('listsGroupsRemove', groupIds)

        //  Update our selected group and list of groups in broadcasts module
        store.commit('broadcastsViewGroupsRemove', groupIds)

        //  Remove this group from our person group
        store.commit('personGroupsRemove', groupIds)

        //  Update our selected group in the autoresponders module
        store.commit('autorespondersSelectedGroupRemove', groupIds)
    },
    groupMembersAdded(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupMembersAdded', notification)

        //  Grab our group id
        const groupId = notification.group_id

        //  Grab the currently selected group
        const selectedGroup = store.getters.listsSelectedGroup

        //  If the selected group doesn't exist then do nothing
        if (!selectedGroup) return

        //  Check if this is the currently selected group
        const match = groupId === selectedGroup.id

        //  If they DON'T match then do nothing
        if (!match) return

        //  Else, grab the person ids from our notification
        const personIds = notification.person_ids

        //  We have to do this so we can make the ids unique
        const uniqueIds = [...new Set([...selectedGroup.persons_ids, ...personIds])]

        //  Update our group's person ids
        selectedGroup.persons_ids = uniqueIds

        //  Update our groups
        groupsSync([selectedGroup])

        //  Get the group members
        store.dispatch('listsSelectedGroupPersonsWhereIn', selectedGroup.persons_ids)
    },
    groupMembersRemoved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupMembersRemoved', notification)

        //  Grab our group id
        const groupId = notification.group_id

        //  Grab the currently selected group
        const selectedGroup = store.getters.listsSelectedGroup

        //  If the selected group doesn't exist then do nothing
        if (!selectedGroup) return

        //  Check if this is the currently selected group
        const match = groupId === selectedGroup.id

        //  If they DON'T match then do nothing
        if (!match) return

        //  Else, grab the person ids from our notification
        const personIds = notification.person_ids

        //  Update our group's person ids by removing the notification ids
        selectedGroup.persons_ids = selectedGroup.persons_ids.filter((x: number) => !personIds.includes(x))

        //  Update our groups
        groupsSync([selectedGroup])

        //  Grab the person ids
        const groupPersonIds = selectedGroup.persons_ids

        //  If it is empty, just set our persons to []
        if (!groupPersonIds || !groupPersonIds.length) store.commit('listsSelectedGroupPersonsSet', [])
        //  Else get the group members
        else store.dispatch('listsSelectedGroupPersonsWhereIn', selectedGroup.persons_ids)
    },
    groupSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupSaved', notification)

        //  Grab our group data
        const data = notification.data

        //  Sync our changes
        groupsSync([data])
    },
    async groupSavedDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: groupSavedDefault', notification)

        //  Grab our group id from the notification
        const groupId = notification.data_id

        //  Find our group from the API (we use wherein because the find will search locally)
        const groups = await store.dispatch('groupsWhereIn', [groupId])

        //  Update our data sets
        groupsSync(groups)
    },
    /**
     * ***************************************
     * Inbox Views
     * ***************************************
     */
    inboxViewDeleted(notification: AppSyncNotification): void {
        store.getters.appDebug && console.log('APPSYNC: inboxViewDeleted', notification)

        //  Grab the group id from the notification
        const groupIds = notification.removed_ids

        //  Remove this group from our inbox views
        store.commit('groupsBucketsRemove', groupIds)

        //  Make sure we update the selected inbox view if needed
        store.commit('inboxSelectedBucketRemove', groupIds)
    },
    inboxViewSaved(notification: AppSyncNotification): void {
        store.getters.appDebug && console.log('APPSYNC: inboxViewSaved', notification)

        //  Grab the updated bucket
        const data = notification.data

        //  Update our bucket groups
        store.commit('groupsBucketsPush', [data])

        //  Update our selected inbox view
        store.commit('inboxSelectedBucketSync', [data])
    },
    async inboxViewSavedDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: inboxViewSavedDefault', notification)

        //  Grab our view id from the notification
        const viewId = notification.data_id

        //  Find our view from the API
        const views = await store.dispatch('groupsWhereIn', [viewId])

        //  Update our bucket groups
        store.commit('groupsBucketsPush', views)

        //  Update our selected inbox view
        store.commit('inboxSelectedBucketSync', views)
    },
    /**
     * ***************************************
     * Invitations
     * ***************************************
     */
    invitationsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invitationsDeleted', notification)

        // Get the invitation id from the notification
        const invitationIds = notification.removed_ids

        //  Remove invitation from global data store
        store.commit('invitationsRemove', invitationIds)

        //  Remove invitation from the view
        store.commit('teammatesInvitationsRemove', invitationIds)
    },
    //  This occurs when an invitation is sent, deleted, or updated
    invitationSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invitationSaved', notification)

        //  Get the invitation data from our notification
        const data = notification.data

        //  Update global dataset
        store.commit('invitationsPush', [data])

        //  Update users view
        store.commit('teammatesInvitationsPush', [data])
    },
    /**
     * ***************************************
     * Invoices
     * ***************************************
     */
    invoiceCampaignSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invoiceCampaignSaved', notification)

        //  Get the invoice campaign data from our notification
        const data = notification.data

        //  Update our invoice campaign in our Vuex Store
        store.commit('invoiceCampaignSet', data)
    },
    invoicesDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invoicesDeleted', notification)

        //  Grab our invoice ids
        const invoiceIds = notification.removed_ids

        //  Update global dataset
        store.commit('invoicesRemove', invoiceIds)

        //  Update payments module
        store.commit('paymentsInvoicesRemove', invoiceIds)

        //  Update person invoices module
        store.commit('personSelectedInvoicesRemove', invoiceIds)
    },
    invoiceSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invoiceSaved', notification)

        //  Get the invoice from our notification
        const data = notification.data

        //  Update global dataset
        store.commit('invoicesPush', [data])

        //  Update payments module
        store.commit('paymentsInvoicesSync', [data])

        //  Update person invoices module
        store.commit('personSelectedInvoicesSync', [data])
    },
    async invoiceSavedDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: invoiceSavedDefault', notification)

        //  Get the invoice id from our notification
        const invoiceId = notification.data_id

        //  Get the data from the API
        const data = await store.dispatch('invoicesFind', invoiceId)

        //  Update global dataset
        store.commit('invoicesPush', [data])

        //  Update payments module
        store.commit('paymentsInvoicesSync', [data])

        //  Update person invoices module
        store.commit('personSelectedInvoicesSync', [data])
    },
    /**
     * ***************************************
     * Media
     * ***************************************
     */
    mediaDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: mediaDeleted', notification)

        //  Grab the storage used from our notification
        const used = notification.used

        //  Grab the storage limit from our notification
        const limit = notification.limit

        //  Set our new storage used and limit
        store.commit('storageUsedSet', used)
        store.commit('storageLimitSet', limit)

        //  Grab the media id from the notification
        const mediaIds = notification.removed_ids

        //  Remove this media from our global dataset
        store.commit('mediaRemove', mediaIds)

        //  Remove this media from our media view
        store.commit('mediaViewMediaRemove', mediaIds)

        //  Remove this media from our inbox
        store.commit('messageMediaRemove', mediaIds)

        //  Remove this media from our person media
        store.commit('personSelectedMediaRemove', mediaIds)
    },
    mediaSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: mediaSaved', notification)

        //  Grab the storage used from our notification
        const used = notification.used

        //  Grab the storage limit from our notification
        const limit = notification.limit

        //  Set our new storage used and limit
        store.commit('storageUsedSet', used)
        store.commit('storageLimitSet', limit)

        //  Grab our media from the notification
        const data = notification.data

        //  Update global dataset
        store.commit('mediaPush', [data])

        //  Update media view
        store.commit('mediaViewMediaSync', [data])

        //  Update our inbox media
        store.commit('messageMediaSync', [data])

        //  Update person media
        store.commit('personSelectedMediaPush', [data])
    },
    /**
     * ***************************************
     * Messages
     * ***************************************
     */
    messageFeedSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: messageFeedSaved', notification)

        //  Grab the message feed top row data from our notification
        const data = notification.data

        //  Set our message top row data
        store.commit('messagesTableDataPush', data)
    },
    messagesDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log("APPSYNC: messagesDeleted", notification)

        //  Grab the message ids from the notification
        const msgIds = notification.removed_ids

        //  Update global messages
        store.commit('messagesRemove', msgIds)

        //  Update inbox messages
        store.commit('inboxSelectedMessagesRemove', msgIds)

        //  Update inbox filtered messages
        store.commit('inboxSelectedFilteredMessagesRemove', msgIds)

        //  Update teammates messages
        store.commit('teammatesSelectedMessagesRemove', msgIds)

        //  Update messages table
        store.commit('messagesTableMessagesRemove', msgIds)

        //  Update payments messages
        store.commit('paymentsSelectedInvoiceMessagesRemove', msgIds)
    },
    //  This handles a message creation event. This is more heavy in the business logic on the client
    //  But in turn it only requires 1 app sync event to be fired for each message sent instead of requiring
    //  3.
    messagePosted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: messagePosted', notification)

        //  Grab the message from our notification
        const data: Message = notification.data

        //  Update our messages
        messagesSync(data)

        //  Sort our inbox channels
        store.commit('inboxChannelsSort')
    },
    //  This handles a message update event. This is more heavy in the business logic in the API
    //  But in turn we get faster updates even if every time this gets fired so do `channelsUpdated`
    //  and `messageFeedUpdated` 
    messageSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: messageSaved', notification)

        //  Grab the updated message
        const data: Message = notification.data

        //  Update global messages
        store.commit('messagesPush', [data])

        //  Update inbox messages
        store.commit('inboxSelectedMessagesSync', [data])

        //  Update inbox filtered messages
        store.commit('inboxSelectedFilteredMessagesSync', [data])

        //  Update teammates messages
        store.commit('teammatesSelectedMessagesSync', [data])

        //  Update messages table
        store.commit('messagesTableMessagesSync', [data])

        //  Update payments messages
        store.commit('paymentsSelectedInvoiceMessagesSync', [data])
    },
    /**
     * ***************************************
     * Notifications
     * ***************************************
     */
    //  Triggers when we mark ALL as read
    notificationsBatchRead(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: notificationsBatchRead', notification)

        //  Grab the sender
        const sender = notification.sender_id

        //  Grab our auth user
        const user = store.getters.user

        //  Check if the sender matches our auth user
        const sameUser = sender === user.id

        //  If they do NOT match, do nothing
        if (!sameUser) return

        //  Grab the notification data
        const data = notification.data

        //  Grab our ids and total from the data and store the additional attributes in attr
        const { total, ...attr } = data

        //  Grab our notifications
        const notifs = store.getters.notifications

        //  Create our array of notifications by combining values with ids
        for (let i = 0; i < notifs.length; i++) {
            //  Update this entry
            Object.assign(notifs[i], attr)
        }

        //  Update notifications view
        store.commit('notificationsPush', notifs)

        //  This only ever gets triggered when we mark all as read, so unread can actually just be an empty array
        store.commit('notificationsUnreadSet', [])

        //  Update our total
        store.commit('notificationsUnreadCountSet', total)
    },
    //  Triggers when marking 1 or more notifications as read
    notificationsRead(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: notificationsRead', notification)

        //  Grab the sender
        const sender = notification.sender_id

        //  Grab our auth user
        const user = store.getters.user

        //  Check if the sender matches our auth user
        const sameUser = sender === user.id

        //  If they do NOT match, do nothing
        if (!sameUser) return

        //  Grab the notification data
        const data = notification.data

        //  Grab our ids and total from the data and store the additional attributes in attr
        const { ids, total, ...attr } = data

        //  Create our array of notifications by combining values with ids
        const notifs = ids.map((x: string) => {
            return { id: x, ...attr }
        })

        //  Update notifications view with these
        store.commit('notificationsPush', notifs)

        //  We actually have to REMOVE these notifications from unread
        store.commit('notificationsUnreadRemove', ids)

        //  Update our total
        store.commit('notificationsUnreadCountSet', total)
    },
    //  Triggers when marking 1 or more notifications as unread
    notificationsUnread(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: notificationsUnread', notification)

        //  Grab the sender
        const sender = notification.sender_id

        //  Grab our auth user
        const user = store.getters.user

        //  Check if the sender matches our auth user
        const sameUser = sender === user.id

        //  If they do NOT match, do nothing
        if (!sameUser) return

        //  Grab the notification data
        const data = notification.data

        //  Grab our ids and total from the data and store the additional attributes in attr
        const { ids, total, ...attr } = data

        //  Grab our notifications from the store
        const notifs = store.getters.notifications

        //  Get all of the notifications matching these ids
        const matches = notifs.filter((x: Notification) => ids.includes(x.id))

        //  Create our array of notifications by combining values with ids
        const updates = matches.map((x: Notification) => {
            return { ...x, ...attr }
        })

        //  Update notifications view with these
        store.commit('notificationsPush', updates)

        //  We actually have to REMOVE these notifications from unread
        store.commit('notificationsUnreadSync', updates)

        //  Update our total
        store.commit('notificationsUnreadCountSet', total)
    },
    //  Triggers when we delete ALL notifications
    notificationsBatchDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: notificationsBatchDeleted', notification)

        //  Grab the sender
        const sender = notification.sender_id

        //  Grab our auth user
        const user = store.getters.user

        //  Check if the sender matches our auth user
        const sameUser = sender === user.id

        //  If they do NOT match, do nothing
        if (!sameUser) return

        //  Set our notifications to an empty array
        store.commit('notificationsSet', [])

        //  Set our notifications component to empty array
        store.commit('notificationsUnreadSet', [])

        //  Set our total to 0
        store.commit('notificationsUnreadCountSet', 0)
    },
    //  Triggers when deleting 1 or more notification
    notificationsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: notificationDeleted', notification)

        //  Grab the sender
        const sender = notification.sender_id

        //  Grab our auth user
        const user = store.getters.user

        //  Check if the sender matches our auth user
        const sameUser = sender === user.id

        //  If they do NOT match, do nothing
        if (!sameUser) return

        //  Grab our total from the payload
        const total = notification.total

        //  Grab the notification ids from the notification
        const notifIds = notification.removed_ids

        //  Update our notification view
        store.commit('notificationsRemove', notifIds)

        //  Update the unread notifications component
        store.commit('notificationsUnreadRemove', notifIds)

        //  Update our total
        store.commit('notificationsUnreadCountSet', total)
    },
    /**
     * ***************************************
     * Office Hours
     * ***************************************
     */
    officeHourSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: officeHourSaved', notification)

        //  Grab our updated office hours from the notification
        const data = notification.data

        //  Get our office hours
        store.commit('officeHoursSet', data)
    },
    /**
     * ***************************************
     * Opt Out
     * ***************************************
     */
    optOutDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: optOutDeleted', notification)

        //  Get the msisdn from the notification
        const msisdn = notification.data

        //  Add the msisdn to the optedOut list
        store.commit('optedOutAdd', msisdn)
    },
    optOutSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: optOutSaved', notification)

        //  Get the msisdn from the notification
        const msisdn = notification.data

        //  Remove the msisdn from the optedOut list
        store.commit('optedOutRemove', msisdn)
    },
    /**
     * ***************************************
     * Payment Fraud
     * ***************************************
     */
    paymentFraud(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: paymentFraud', notification)

        //  Pop a popup informing what happened
        store.dispatch('dialogOpen', {
            type: 'MESSAGE',
            title: 'Contact Us',
            text: `We have detected some suspicous behavior on your account. Please contact us for more information. <a href="mailto:support@messagedesk.com">support@messagedesk.com</a>`,
            icon: 'mdi-alert',
            color: 'primary'
        })
    },
    /**
     * ***************************************
     * Persons
     * ***************************************
     */
    //  This is for mobile imports
    personBatchImportComplete(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: personBatchImportComplete', notification)

        //  Pop a banner telling the user how many contacts have been added
        store.commit('appNotificationBarSet', {
            message: `${notification.data} Contacts have been imported into MessageDesk from your mobile device!`,
            type: 'success',
            closeable: true
        })

        //  Refresh the contacts and groups
        store.dispatch('personsPaginate')
        store.dispatch('groupsGet')
    },
    personsDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: personsDeleted', notification)

        //  Get the list of person ids that were deleted
        const personIds = notification.removed_ids

        //  Update our global dataset
        store.commit('personsRemove', personIds)

        //  Update our autocomplete
        store.commit('personsAutocompleteRemove', personIds)

        //  Update contacts persons
        store.commit('contactsPersonsRemove', personIds)

        //  Update contacts searched persons
        store.commit('contactsSearchedPersonsRemove', personIds)

        //  Update our selected group persons
        store.commit('listsSelectedGroupPersonsRemove', personIds)

        //  Update inbox selected person
        store.commit('inboxSelectedPersonRemove', personIds)

        //  Update users view selected person
        store.commit('teammatesSelectedPersonRemove', personIds)

        //  Remove all invoices belonging to this person?
    },
    personsDeletedDefault(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: personsDeletedDefault', notification)

        //  Just refresh our persons
        store.dispatch('personsPaginate')
    },
    personsSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: personSaved', notification)

        //  Grab our updated person from our data
        const data = notification.data

        //  Update global dataset
        store.commit('personsPush', [data])

        //  Update contacts selected person
        store.commit('contactsPersonsSync', [data])

        //  Update contacts searched persons
        store.commit('contactsSearchedPersonsSync', [data])

        //  Update our selected group persons
        store.commit('listsSelectedGroupPersonsSync', [data])

        //  Update inbox selected person
        store.commit('inboxSelectedPersonSync', [data])

        //  Update users view selected person
        store.commit('teammatesSelectedPersonSync', [data])

        //  Update inbox channels
        store.commit('inboxChannelsPersonsUpdate', [data])

        //  Update payments
        store.commit('paymentsInvoicesPersonsSync', data)
    },
    /*****************************************
     * Phone Orders
     * ***************************************
     */
    phoneOrderSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: phoneOrderSaved', notification)

        //  Grab our updated phone order from our data
        const data = notification.data

        //  Updated phone order in the store
        store.commit('phoneOrderSet', data)
    },
    /**
     * ***************************************
     * Routes
     * ***************************************
     */
    routeDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: routeDeleted', notification)

        //  Get the list of route ids that were deleted
        const routeIds = notification.removed_ids

        //  Tell our lists to remove this route
        store.commit('routesRemove', routeIds)
        store.commit('routesViewRoutesRemove', routeIds)
        store.commit('messageLinksRemove', routeIds)
    },
    routeSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: routeSaved', notification)

        //  Grab our route from the notification
        const data = notification.data

        //  Else, tell our lists to update
        store.commit('routesPush', [data])
        store.commit('routesViewRoutesSync', [data])
        store.commit('messageLinksPush', [data])
    },
    async routeSavedDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: routeSavedDefault', notification)

        //  Get the route id from our notification
        const routeId = notification.data_id

        //  Get the data from the API
        const data = await store.dispatch('routesFind', routeId)

        //  Else, tell our lists to update
        store.commit('routesPush', [data])
        store.commit('routesViewRoutesSync', [data])
        store.commit('messageLinksPush', [data])
    },
    /**
     * ***************************************
     * Search
     * ***************************************
     */
    searchImportStarted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: searchImportStarted', notification)

        //  Pop a dialog informing the user that we are importing contacts
        store.dispatch('dialogOpen', {
            type: 'MESSAGE',
            title: 'Search Import Started',
            icon: 'mdi-find-replace',
            text: `Please be patient while we import all of your contacts into our search engine for an optimal user experience. We will send you a notification once the import has been finished.`
        })

        //  Disable the search bar while importing
        store.commit('searchBarToggle', false)
    },
    searchImportCompleted(notification: AppSyncNotification): void {
        //  Update for AppSync 2.0
        store.getters.appDebug && console.log(`APPSYNC: searchImportCompleted`, notification)

        //  Enable the search bar now that we finished importing
        store.commit('searchBarToggle', true)
    },
    /**
     * ***************************************
     * Settings
     * ***************************************
     */
    settingSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: settingSaved', notification)

        //  Grab our updated setting from the notification
        const data = notification.data

        //  Update our settings with this setting
        store.commit('settingsPush', [data])
    },
    /**
     * ***************************************
     * Sms Accounts
     * ***************************************
     */
    smsAccountSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: smsAccountSaved', notification)

        //  Grab our updated sms account from the notificaiton
        const data = notification.data

        //  Update our sms account with this account
        store.commit('smsAccountSet', data)
    },
    /**
     * ***************************************
     * Stripe/Subscriptions
     * ***************************************
     */
    stripeInvoicePaid(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: stripeInvoicePaid', notification)

        //  Update our user's subscription to get the new status
        store.dispatch('subscriptionGet')

        //  Update our billing history for the user to see the change
        store.dispatch('stripeInvoicesPaginate', { tenant_id: store.getters.tenant.id })

        //  Refresh the tenant, to get updated credits balance
        store.dispatch('tenantGet', store.getters.tenant.id)
    },
    async subscriptionUpdated(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: subscriptionUpdated', notification)

        //  Grab our tenant from the subscription
        const data = notification.data

        //  Update our tenant with this
        store.commit('tenantSet', data)

        //  Update our subscription
        const sub = await store.dispatch('subscriptionGet')

        //  Check our sub status
        const status = sub.status

        //  If we ARE canceled then we don't need to do anything
        if (status === 'canceled') return

        //  Else, we are NOT canceled so we need to find the newest invoice
        store.dispatch('stripeInvoiceFind', {
            tenant_id: data.id
        })
    },
    //  This recieves our NewPayingCustomer event from the api so that we can send it to Segment
    //  and let GTAG Manager recieve this event since it can only recieve client side events
    async firstStripePayment(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: FirstStripePayment', notification)

        //  Grab our current logged in user
        const user = store.getters.user

        //  Grab our current tenant the user is on
        const tenant = store.getters.tenant

        //  Grab the plan assocaited with this tenant
        const plan = store.getters.plan

        //  If no plan, return 
        if (!plan) return 

        //  If product tier not specified, return 
        if (!plan.metadata || !plan.metadata.product_tier) return 

        //  Get our subscription
        const sub = await store.dispatch('subscriptionGet')

        //  Check our sub status
        const status = sub.status

        //  If we ARE canceled then we don't need to do anything
        if (status === 'canceled') return

        //  Else, we are NOT canceled so we send the event to segment
        Analytics.segmentTrack('NewPayingCustomer', {
            user_id: user.id,
            tenant_id: tenant.id,
            plan: plan.metadata.product_tier,
            length: plan.interval,
            cost: plan.amount,
            payment_card: true
        })
    },
    
    /**
     * ***************************************
     * Templates
     * ***************************************
     */
    templatesDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: templatesDeleted', notification)

        //  Grab the template ids from the notification
        const templateIds = notification.removed_ids

        //  Remove these templates from the global set
        store.commit('templatesRemove', templateIds)

        //  Remove the templates from the templates view
        store.commit('templatesViewTemplatesRemove', templateIds)

        //  Remove these templates from the message text form
        store.commit('messageTemplatesRemove', templateIds)
    },
    templateSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: templateSaved', notification)

        //  Grab the template from our notification
        const data = notification.data

        //  Update our global state
        store.commit('templatesPush', [data])

        //  Update our templates view
        store.commit('templatesViewTemplatesPush', [data])

        //  Update our message text form
        store.commit('messageTemplatesPush', [data])
    },
    /**
     * ***************************************
     * Tenants
     * ***************************************
     */
    tenantDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: tenantDeleted', notification)

        //  Set our tenant to null
        store.commit('tenantSet', null)

        //  Since the tenant is deleted, push user to home page
        router.push('/')
    },
    tenantMetricsUpdated(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: tenantMetricsUpdated', notification)

        //  Grab the sender id
        const sender_id = notification.sender_id

        //  Check if we have a sender AND if it matches the current user
        //  We allow a non-sender event to come through since our dashboard metrics have
        //  data that EVERY user sees, and data that ONLY the auth user sees
        const proceed = !sender_id || sender_id === store.getters.user.id

        //  If we do not want to proceed, then stop here
        if (!proceed) return

        //  Grab our updated metrics
        const data = notification.data

        //  Update our tenant metrics
        store.commit('tenantMetricsSet', data)
    },
    tenantSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: tenantSaved', notification)

        //  Get the tenant from our notification
        const data = notification.data

        //  Grab our current tenant
        const tenant = store.getters.tenant

        //  GRab the data provider
        const provider = tenant.data_provider

        //  Update our tenant
        store.commit('tenantSet', data)

        //  If our data provider changed from null to QBO then get our default template
        if (provider !== 'QBO' && data.data_provider === 'QBO') {
            store.dispatch('templatesGetDefault', { type: 'invoice' })
        }
    },
    tenantSuspended(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: tenantSuspended', notification)

        //  Grab our tenant
        const tenant = store.getters.tenant

        //  Set the suspended at field on our tenant (doesn't need to be accurate)
        tenant.suspended_at = new Date().toISOString()

        //  Set our tenant
        store.commit('tenantSet', tenant)

        //  Disconnect from Pusher to avoid stacking these connections
        window.Echo.disconnect()
    },
    /**
     * ***************************************
     * Tenant Memberships
     * ***************************************
     */
    tenantMembershipDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        //  Feel free to modify the behavior of this app sync event
        store.getters.appDebug && console.log('APPSYNC: tenantMembershipDeleted', notification)

        //  Grab the ids that were deleted
        const membershipIds = notification.removed_ids

        //  Grab our current membership
        const curr = store.getters.currentMembership

        //  Check if our current membership id is in our removed
        const deleted = membershipIds.some((x: number | string) => x === curr.id)

        //  If it wasn't deleted, do nothing
        if (!deleted) return

        //  Else, we need to logout
        store.dispatch('logout').then(() => {
            router.push('/')
        })
    },
    tenantMembershipSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: tenantMembershipSaved', notification)

        //  Grab the membership from our notification
        const data = notification.data

        //  Grab all of our users from the store
        const users = store.getters.users

        //  Find the user matching this membership id
        const user = users.find((x: User) => x.id === data.user_id)

        //  If the user don't exist then do nothing
        if (!user) return

        //  Find the index with matching tenant id and user id
        const idx = user.roles.findIndex((x: UserRole) => {
            return x.pivot.tenant_id === data.tenant_id
        })

        //  Grab that role
        const current = user.roles[idx]

        //  Our updated role
        const role = {...current, ...data.role}

        //  Apply the roles to our user
        user.roles[idx] = role

        //  Apply the memberships to our user
        user.memberships = window.removeDuplicatesById(user.memberships, [data])

        //  Update global dataset
        store.commit('usersPush', [user])

        //  Update teammates
        store.commit('teammatesUsersPush', [user])

        //  Our callback to perform if the auth user DOES match this current user
        const cb = (auth: User): void => {
            //  Else, update our existing memberships
            store.commit('tenantMembershipsPush', [data])

            //  We have to find the role matching the user and tenant id
            const authIdx = auth.roles.findIndex((x: UserRole) => {
                return x.pivot.tenant_id === data.tenant_id
            })

            //  Update our auth user (we use window._.extend so reactivity picks up)
            auth.roles[authIdx] = window._.extend(auth.roles[authIdx], role)

            //  Update our auth memberships too
            auth.memberships = window.removeDuplicatesById(auth.memberships, [data])

            //  Update the store
            store.commit('authUserSet', auth)
        }

        //  Handle our auth user syncing
        authSync(user, cb)
    },
    /**
     * ***************************************
     * Users
     * ***************************************
     */

    //  Handles teammates channels update
    teammatesChannelSaved(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: teammatesChannelSaved', notification)

        //  Grab our channel data
        const data = notification.data

        //  Update teammates
        store.commit('teammatesSelectedChannelSync', [data])
    },
    userEmailVerified(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: userEmailVerified', notification)

        //  If a tenant does NOT exist, we don't need to update the tenant dataset (this occurs during registration, before the tenant is built)
        if (!store.getters.tenant) return

        //  Grab the user from the notification
        const data = notification.data

        //  Update global dataset
        store.commit('usersPush', [data])

        //  Update users view
        store.commit('teammatesUsersPush', [data])

        //  Grab the auth user
        const auth = store.getters.user

        //  Check if the auth user matches our user from the notification
        const match = auth.id === data.id

        //  If this is not the auth user then we are done
        if (!match) return

        //  Take away our banner
        store.commit('appNotificationBarSet', null)
    },
    userMobilePhoneVerified(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: userMobilePhoneVerified', notification)

        //  If a tenant does NOT exist, we don't need to update the tenant dataset (this occurs during registration, before the tenant is built)
        if (!store.getters.tenant) return

        //  Grab the user from the notification
        const data = notification.data

        //  Update global dataset
        store.commit('usersPush', [data])

        //  Update users view
        store.commit('teammatesUsersPush', [data])

        //  Grab the auth user
        const auth = store.getters.user

        //  Check if the auth user matches our user from the notification
        const match = auth.id === data.id

        //  If this is not the auth user then we are done
        if (!match) return

        //  Take away our banner
        store.commit('appNotificationBarSet', null)
    },
    usersDeleted(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: usersDeleted', notification)

        //  Grab our total from the payload
        const total = notification.total

        //  Grab the user ids from the notification
        const userIds = notification.removed_ids

        //  Update global dataset
        store.commit('usersRemove', userIds)

        //  Update users view
        store.commit('teammatesUsersRemove', userIds)

        //  Update user count
        store.commit('userCountSet', total)
    },
    //  Handles a new user being created
    async userSaved(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: userSaved', notification)

        //  Grab our total from the notification
        const total = notification.total

        //  Grab the user id from the notification
        const userId = notification.user_id

        //  Get the user from the API
        const user = await store.dispatch('usersFind', userId)

        //  Update global dataset
        store.commit('usersPush', [user])

        //  Update users view
        store.commit('teammatesUsersPush', [user])

        //  Update our count
        store.commit('userCountSet', total)
    },
    //  Handles a user being updated
    userUpdated(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: userUpdated', notification)

        //  Grab our total from the notification
        const total = notification.total

        //  Grab the user from the notification
        const data = notification.data

        //  Update global dataset
        store.commit('usersPush', [data])

        //  Update users view
        store.commit('teammatesUsersPush', [data])

        //  Update our count
        store.commit('userCountSet', total)

        //  Our callback for if this user matches our auth user
        const cb = (auth: User) => {
            //  Grab ONLY the properties that might change
            const props = [
                'firstname',
                'lastname',
                'email',
                'mobilephone',
                'logo_route_id',
                'logo_route'
            ]

            //  Grab just these properties from our user
            const updates = window._.pick(data, props)

            //  Update the auth user
            auth = window._.extend(auth, updates)

            //  Update in the store
            store.commit('authUserSet', auth)
        }

        //  Apply auth sync
        authSync(data, cb)
    },
    async userUpdatedDefault(notification: AppSyncNotification): Promise<void> {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: userUpdatedDefault', notification)

        //  Grab the user id from our notification
        const userId = notification.data_id

        //  Fetch the user from the API
        const user = await store.dispatch('usersFind', userId)

        //  Grab our total from the notification
        const total = notification.total

        //  Update global dataset
        store.commit('usersPush', [user])

        //  Update users view
        store.commit('teammatesUsersPush', [user])

        //  Update our count
        store.commit('userCountSet', total)

        //  Our callback for if this user matches our auth user
        const cb = (auth: User) => {
            //  Grab ONLY the properties that might change
            const props = [
                'firstname',
                'lastname',
                'email',
                'mobilephone',
                'logo_route_id',
                'logo_route'
            ]

            //  Grab just these properties from our user
            const updates = window._.pick(user, props)

            //  Update the auth user
            auth = window._.extend(auth, updates)

            //  Update in the store
            store.commit('authUserSet', auth)
        }

        //  Apply auth sync
        authSync(user, cb)
    },
    usersMetricsUpdated(notification: AppSyncNotification): void {
        //  Updated for AppSync 2.0
        store.getters.appDebug && console.log('APPSYNC: usersMetricsUpdated', notification)

        //  Grab our updated metrics
        const metrics = notification.data

        //  Update our store
        store.commit('usersMetricsSet', metrics)
    }
}

//  Export public functions
export default {
    //  Handles the appsync notification received event
    onAppSyncNotificationReceived(notification: AppSyncNotification): void {
        //  Define an ignore list (we use this since some 'appsync' notifications are not actually for appsync)
        const ignore = ['qboOauthComplete', 'xeroOAuthConnectComplete']

        //  If the event handler is in the ignore list, do nothing
        if (ignore.includes(notification.event)) return

        //  Else, find the event handler for the notification
        const handler = handlers[notification.event]

        //  If the handler does NOT exist, throw error
        if (!handler) throw `Missing AppSync Handler: ${notification.event}`

        //  Else, run the handler
        handler(notification)

        //  Grab the type
        const type = notification.type

        //  If this is NOT a TenantNotification then we are done
        if (type !== 'App\\Notifications\\TenantNotification') return

        //  Update the notification id to be our app sync id
        const { appsync_id, ...payload } = notification

        //  Change our id
        payload.id = appsync_id

        //  Else, sync the changes
        store.commit('appSyncNotificationsPush', [payload])
    },
    onAppSyncNotificationsRefreshed(notifications: AppSyncNotification[]): void {
        //  If we have 0 notifications then do nothing
        if (!notifications.length) return store.commit('appNotificationBarSet', null)

        //  Else, iterate through each notification
        notifications.forEach((x: AppSyncNotification) => {
            //  Clone our notification but exclude "data"
            const { data, ...clone } = x

            //  Attach data to our notification
            const notification = {...clone, ...data}

            //  Find the handler for this notification
            const handler = handlers[x.event]

            //  If the handler does NOT exist, just skip
            if (!handler) return

            //  Else, run the handler
            handler(notification)
        })

        //  Remove our banner
        store.commit('appNotificationBarSet', null)
    },
    onBroadcastsPosted(broadcasts: Broadcast[]): void {
        //  Sync our changes
        store.commit('broadcastsPush', broadcasts)

        //  Update the broadcasts view (sync instead of push means it only gets updated, not added)
        store.commit('broadcastsViewBroadcastsSync', broadcasts)
    },
    //  Handles a messages posted event (occurs when a user sends an outbound message)
    onMessagesPosted(message: Message) {
        //  Check if we are on the mobile app
        const isApp = store.getters.isApp

        //  If we are NOT on a mobile app, play the sound
        if (!isApp) window.sounds.play('swipe')

        //  Sync the changes
        messagesSync(message)

        //  Sort our inbox channels
        store.commit('inboxChannelsSort')
    },
    //  Handles the user login event
    onUserLogin: async (user: User): Promise<void> => {
        store.getters.appDebug && console.log('APPSYNC: onUserLogin')

        //  Fetch the stripe customer
        if (user.stripe_customer_id) store.dispatch('stripeCustomerFind', true)

        //  Fetch user memberships
        await store.dispatch('tenantMembershipsGet')

        //  Fetch subscription options
        await store.dispatch('subscriptionOptionsGet')

        //  Send segment an event with data needed by MCS
        Analytics.segmentTrack('UserLogin', {
            user_id: user.id,
            user_email: user.email,
            user_firstname: user.firstname,
            user_lastname: user.lastname,
            user_mobilephone: user.mobilephone
        })
    },
    //  Handles the user logout event
    onUserLogout() {
        store.getters.appDebug && console.log('APPSYNC: onUserLogout')

        //  Clear all tenant data (setting tenant to null calls async tenantReset afterward)
        store.commit('tenantSet', null)

        //  Clear all user data (data persisted outside the tenant context)
        store.commit('membershipsReset')

        //  Clear local storage
        localStorage.clear()
    },
    //  Handles the user notification received event
    onUserNotificationReceived(notification: UserNotification): void {
        //  Grab our tenant
        const tenant = store.getters.tenant

        //  If there is no tenant then do nothing
        if (!tenant) return

        //  Check if we should process the notification (ie, we only want to handle notification in the current tenant context)
        const shouldProcess = tenant && notification.tenant_id === tenant.id

        //  If we should NOT process the notification, do nothing
        if (!shouldProcess) return

        //  Update the notification id to be our app sync id
        const { appsync_id, appsync_type, category, created_at, type, ...data } = notification

        //  If this is NOT a UserNotification then we are done
        if (type !== 'App\\Notifications\\UserNotification') return

        //  Get the user
        const user = store.getters.user

        //  @ts-ignore Add the type to our payload
        data.type = appsync_type

        //  Our payload will be a compound object
        const payload = {
            id: appsync_id,
            category: category,
            data: data,
            notifiable_id: user.id,
            notifiable_type: "App\\Models\\Main\\User",
            object: 'Notification',
            read_at: null,
            type: type,
            created_at: created_at
        }

        //  Else, sync the changes
        store.commit('notificationsPush', [payload])

        //  Update unread notifications component
        store.commit('notificationsUnreadSync', [payload])

        //  Grab our current notifications total
        const total = store.getters.notificationsUnreadCount

        //  Update our total
        store.commit('notificationsUnreadCountSet', total + 1)
    },
    //  Handles the tenant disconnected event
    onTenantRemoved: async () => {
        store.getters.appDebug && console.log('APPSYNC: onTenantRemoved')

        //  Reset the tenant store (removes all tenant data)
        await store.dispatch('tenantReset')
    },
    //  Handles the new tenant set event
    onTenantSet: async (tenant: Tenant, silent: boolean = false) => {
        store.getters.appDebug && console.log('APPSYNC: onTenantSet', tenant, silent)

        //  Get the tenant's name
        const name = filters.tenantName(tenant)

        //  Display the splash screen
        if (!silent) store.commit('splashSet', { message: `Loading ${name}...` })

        //  Erase tenant data from vuex store IF this is a new tenant
        if (!silent)
        {
            //  This resets our vuex store for all tenant scoped data
            await store.dispatch('tenantReset');
            //  This will reset the tenant metrics
            store.commit('tenantMetricsSet', null)
        }

        //  Reset the tenant key so we don't persist ANY STATE from previous tenant (this wipes all tenant data loaded in the DOM)
        if (!silent) store.commit('tenantKeySet', `${tenant.subdomain}Key`)

        //  We use a set timeout here to make sure the Vue instance is loaded, before we handle the event (a race-condition can occur on initial load)
        setTimeout(async () => {

            //  Fetch critical data synchronously
            if (!silent) store.commit('splashSet', { message: `Loading Subscription...` })
            await store.dispatch('tenantInitialStateGet')       //  Initial State
            await store.dispatch('subscriptionGet')             //  Tenant subscription
            await store.dispatch('appSyncNotificationsGet')     //  App Sync notifications

            if (!silent) store.commit('splashSet', { message: `Loading Users...` })
            const users = await store.dispatch('usersGet')      //  Tenant Users
            await store.dispatch('personsWhereInUsers', users.map((x: User) => x.id))

            if (!silent) store.commit('splashSet', { message: `Loading Inbox...` })
            await store.dispatch('inboxChannelsPaginate')       //  Inbox channels
            await store.dispatch('groupsBucket')                //  Inbox views
            await store.dispatch('broadcastsViewBroadcastsPaginate')  //  Broadcasts Module

            //  Remove the loading screen
            if (!silent) store.commit('splashSet', null)

            //  Fetch Modules (in order of priority)
            store.dispatch('personsPaginate')                   //  Contacts Module
            store.dispatch('groupsGet')                         //  Groups Module
            store.dispatch('groupsOptOut')                      //  Broadcasts opt group
            store.dispatch('templatesGet')                      //  Templates Module
            store.dispatch('optedOutGet')                       //  Opted Out Persons
            store.dispatch('autorespondersCampaignsPaginate')   //  Autoresponders Module
            store.dispatch('notificationsUnreadGet')            //  Unread user notifications
            store.dispatch('notificationsPaginate')             //  Notifications Module
            store.dispatch('brandGet')                          //  A2P Registration Module
            store.dispatch('officeHoursGet')                    //  OfficeHours Module
            store.dispatch('routesPaginate')                    //  ShortLinks Module
            store.dispatch('formsGet')                          //  Forms Module
            store.dispatch('chatbotsGet')                       //  Chatbots Module
            //  Fetch subscription options
            store.dispatch('subscriptionOptionsGet')

            //  Fetch Other Resources
            store.dispatch('rolesGet')                          //  User Roles
            store.dispatch('invitationsGet')                    //  User Invitations
            store.dispatch('extensionOptionsAll')               //  Contacts custom properties
            store.dispatch('messageTags')
            store.dispatch('messageMediaPaginate')


            //  Check if the tenant can see the Invoices/Payments module (ie. QBO or Xero data provider)
            const hasInvoices = tenant.data_provider === 'QBO' || tenant.data_provider === 'XERO'

            //  If the tenant has invoices, fetch the Invoices/Payments Module
            if (hasInvoices) {
                store.dispatch('qboAccountGet')
                store.dispatch('templatesGetDefault', { type: 'invoice' })
                store.dispatch('paymentsInvoicesPaginate')
                store.dispatch('invoiceCampaignGet')
            }

        }, 1)
    },
    onInboxReconnect() {
        //  Check if there is a selected inbox channel
        const channel = store.getters.inboxSelectedChannel

        //  If there isn't, we are done
        if (!channel) return

        //  Let's clear the cache
        store.commit('messagesReset')

        //  Else, fetch their channel, messages, person, and scheduled messages
        store.dispatch('inboxSelectedChannelGet', channel.id)
        store.dispatch('inboxSelectedMessagesPaginate', channel.id)
        store.dispatch('inboxSelectedPersonGet', channel)
        store.dispatch('inboxSelectedScheduledMessagesGet', channel.id)
    },
    //  Handle websockets reconnecct. If we lost our connection the local dataset is out of sync, so we need to refresh it
    onWebsocketsReconnected() {
        store.getters.appDebug && console.log('APPSYNC:  onWebsocketsReconnected')

        //  Pop a banner telling the user we are reconnecting
        store.commit('appNotificationBarSet', {
            message: 'Reconnecting to Network',
            type: 'error',
            closeable: true
        })

        //  Reload the user
        this.onUserLogin(store.getters.user)

        //  Grab the latest app sync notification
        const latest = store.getters.latestAppSync

        //  If we don't have ANY app sync notifs, then tell our api to update
        if (latest) return store.dispatch('appSyncUpdate', latest.id)

        //  Else, reload the tenant
        this.onTenantSet(store.getters.tenant, true)

        //  Reload the current state
        this.onInboxReconnect()

        //  Remove our banner
        store.commit('appNotificationBarSet', null)
    }
}
